import { gql } from '@apollo/client';
import { OrganisationEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import { buildSearchConditions } from 'Util/GraphQLUtils';
import { ORGANISATIONS_PAGE_LENGTH } from 'Views/Components/Clients/ClientList';
import { ComboboxOption } from 'Views/Components/Combobox/Combobox';
import { IWhereCondition } from 'Views/Components/ModelCollection/ModelQuery';

type GqlOrganisationEntityResponse = {
	organisationEntitys: OrganisationEntity[];
}

export type RequestOrganisationArgs = {
	// Filter Organisation entities for primary entity name
	filterByName?: string;

	// Filter Organisation entities that have monitor enabled
	filterByMonitor?: boolean;
}

type RequestOrgClientArgs = {
	search: string,
	sortColumn: string,
	sortDescending: boolean,
	page: number,
	setCount: (newCount: number) => void,
	noPagination?: boolean
}

export async function requestOrganisation(args: RequestOrganisationArgs) {
	const { filterByName, filterByMonitor } = args;
	const whereCondtitions: IWhereCondition<any>[] = [];

	if (filterByName) {
		whereCondtitions.push({
			path: 'primaryBusinessEntity.name',
			comparison: 'like',
			value: [!!filterByName ? `%${filterByName}%` : '%'],
			case: 'CURRENT_CULTURE_IGNORE_CASE',
		});
	}

	if (filterByMonitor !== undefined) {
		whereCondtitions.push({
			path: 'intelEnabled',
			value: filterByMonitor ? 'true' : 'false',
			comparison: 'equal',
		});
	}

	const result = await store.apolloClient
		.query<GqlOrganisationEntityResponse>({
			query: gql`
                    query fetchOrganisations($whereConditions: [WhereExpressionGraph]) {
                        organisationEntitys (
                            where: $whereConditions,
                            orderBy: [{path: "primaryBusinessEntity.name", descending: false}],
                        ) {
                            id
                            intelEnabled
                            approveEnabled
                            isValidated
                            isActive
                            primaryBusinessEntity {
                                id
                                name
                                enabledForMonitor
                            }
                        }
                    }
                `,
			variables: {
				whereConditions: whereCondtitions,
			},
			fetchPolicy: 'no-cache',
		});
	return result;
}

export async function fetchTopbarOrganisationOptions(locationPath: string, filterByName?: string) {
	try {
		const response = await requestOrganisation({ filterByName: filterByName });
		const data = response.data.organisationEntitys;
		if (data === undefined) return [];

		const options = data
			// Should not be able to switch to an organisation that is not active or not validated as our UI doesn't handle workflows for these 
			.filter(x => x.isValidated == true && x.isActive == true)
			.map(org => {
				let canSelect = true;
				// Don't let super user switch to an organisation that can not see the current route
				if (locationPath.includes("monitor") && org.intelEnabled !== true) canSelect = false;
				if (locationPath.includes("approve") && org.approveEnabled !== true) canSelect = false;

				const option: ComboboxOption<string> = {
					disabled: !canSelect,
					display: org.primaryBusinessEntity.name,
					value: org.id,
				};
				return option;
			});
		return options;
	} catch (e) {
		return [];
	}
}

export async function getOrganisationClients(args: RequestOrgClientArgs) {
	const {
		search,
		sortColumn,
		sortDescending,
		page,
		setCount,
		noPagination
	} = args;

	// If there is a search included, create the search param for the query
	const comparison = 'INVARIANT_CULTURE_IGNORE_CASE';
	let searchConditions: IWhereCondition<string>[] = [];
	if (!!search) {
		searchConditions = buildSearchConditions(['primaryBusinessEntity.name',
			'primaryBusinessEntity.abn'], search, comparison);
	}

	// Aggregate conditions into single nested list
	const allConditions: IWhereCondition<string>[][] = [];
	if (searchConditions.length > 0) {
		allConditions.push(searchConditions);
	}

	// Add sorting
	const orderBy: { path: string, descending: boolean }[] = [];
	orderBy.push({ path: sortColumn, descending: sortDescending });

	const query = gql`
		query organisationEntitys(
			$orderBy: [OrderByGraph],
			$take: Int,
			$skip: Int,
			$conditions: [[WhereExpressionGraph]]
		) {
			organisationEntitys(conditions: $conditions, skip: $skip, take:$take, orderBy: $orderBy) {
				id
				created
				isActive
				accountType
				organisationCategory
				customerCount
				approveEnabled
				intelEnabled
				ppsrEnabled
				isReferralPartner
				isValidated
				isOnFreeTrial
				freeTrialExpiryDate
				legacyID
				enforceOnlinePayments
				primaryBusinessEntity {
					id
					name
					abn
					email
					address {
						state
					}
				}
				registration {
					id
					referringOrganisation {
						primaryBusinessEntity {
							id
							name
						}
					}
				}
				users {
					id
				}
				customerss {
					id
					monitoredStatus
				}
			}
			${!noPagination ? `countOrganisationEntitys(conditions: $conditions) {
				number
			}` : ''}
		}
	`;

	const { data } = await store.apolloClient.query({
		query: query,
		fetchPolicy: 'network-only',
		variables: {
			orderBy: orderBy,
			take: !noPagination ? ORGANISATIONS_PAGE_LENGTH : null,
			skip: !noPagination ? ORGANISATIONS_PAGE_LENGTH * (page || 0) : null,
			conditions: allConditions,
		},
	});

	if (!noPagination) setCount(data?.countOrganisationEntitys?.number);

	return data.organisationEntitys.map((organisation: any) => new OrganisationEntity(organisation)) as OrganisationEntity[];
};